import React, {Dispatch, SetStateAction, useRef, useState} from 'react';
import {MarketItem} from './MarketItem';
import {marketColTitlesByMarketGroup, MarketGroup} from "../../../../markets/marketMapping";
import {MarketsSelectPopup} from "./MarketsSelectPopup";
import {useTranslation} from "react-i18next";
import {hex2rgba} from "../../../../utils/ColorUtils";
import { ReactComponent as ArrowDownIcon } from "../../icons/round-arrow_drop_down.svg";
import { createPortal } from 'react-dom';

export type MarketGroupItem = {
    display: boolean;
    marketGroup: MarketGroup;
}


type MarketSelectionMenuProps = {
    open?: boolean;
    marketGroupItems: MarketGroupItem[];
    updateMarketGroupItems: (updater: ((prev: MarketGroupItem[]) => MarketGroupItem[]) | MarketGroupItem[]) => void;
    color?: string

};
export const MarketSelectionMenu = React.memo((props: MarketSelectionMenuProps) => {
        const [currentMarketGroup, setCurrentMarketGroup] = useState<MarketGroup>({} as MarketGroup);
        const { open = false, updateMarketGroupItems, marketGroupItems } = props;
        const [marketGroupButtonEl, setMarketGroupButtonEl] = useState<Element | null>(null);
        const [openMarketGroupsPopup, setOpenMarketGroupsPopup] = useState(false);

        const handleReplace = (selectedMarketGroup: MarketGroup) => {
            onClose();

            updateMarketGroupItems(prev =>
                prev.map(m => {
                    if (m.marketGroup === currentMarketGroup) return { ...m, marketGroup: selectedMarketGroup };
                    if (m.marketGroup === selectedMarketGroup) return { ...m, marketGroup: currentMarketGroup };
                    return m;
                })
            );
        };

        const handleButtonClick = (group: MarketGroup, event: React.MouseEvent) => {
            setCurrentMarketGroup(group);
            setMarketGroupButtonEl(event.currentTarget);
            setOpenMarketGroupsPopup(prev => !prev);
        };

        const onClose = () => setOpenMarketGroupsPopup(false);

        return (
            <div className="flex gap-[10px] pt-[6px]">
                <MarketsSelectPopup
                    open={openMarketGroupsPopup}
                    anchorEl={marketGroupButtonEl}
                    replaceMarketGroups={handleReplace}
                    items={marketGroupItems.filter(el => el.marketGroup !== currentMarketGroup)}
                    onClose={onClose}
                />

                {open && marketGroupItems.filter(el => el.display).map((selMarGroup, index) => (
                    <MarketItem
                        key={index}
                        currentMarketGroupItem={selMarGroup}
                        updatePopupList={handleButtonClick}
                        columnTitles={marketColTitlesByMarketGroup(selMarGroup.marketGroup)}
                        color={hex2rgba(props.color, 0.25)}
                    />
                ))}
            </div>
        );
    }, (prevProps, nextProps) =>
        prevProps.open === nextProps.open &&
        prevProps.marketGroupItems === nextProps.marketGroupItems &&
        prevProps.color === nextProps.color
);



interface MobileMarketGroupsProps {
    marketGroupItems: MarketGroupItem[],
    currentMarketGroup: MarketGroup,
    callback: (group: MarketGroup) => void,
    color: string
}

export const MobileMarketGroups: React.FC<MobileMarketGroupsProps> = ({marketGroupItems, currentMarketGroup, callback, color}) => {

    const {t} = useTranslation();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const buttonRef = useRef<HTMLDivElement>(null);

    const handleSelectGroup = (group: MarketGroup) => {
        callback(group);
        setIsOpen(false);
    };

    const getDropdownStyles = (): React.CSSProperties => {
        if (!buttonRef.current) return {};
        const rect = buttonRef.current.getBoundingClientRect();
        return {
            position: 'fixed',
            top: `${rect.bottom + 10}px`, // 10px отступ снизу
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
        };
    };

    return (
      <div className="max-w-[240px] mx-auto my-[10px]">
          <div ref={buttonRef} onClick={() => setIsOpen(!isOpen)} className={`bg-[#214A32] flex items-center justify-center text-[10px] font-normal rounded-[10px] px-[12px]`}>
              {t(currentMarketGroup)} <ArrowDownIcon/>
          </div>
          {isOpen && createPortal(
              <>
                  <div
                      className="fixed inset-0 z-[9998]"
                      onClick={() => setIsOpen(false)}
                  />
                  <div style={getDropdownStyles()}>
                      <ul className="w-[270px] h-auto shadow-md rounded-md border bg-white [&>*]:border-[#96A6B0] [&>*]:border-b [&>*:last-child]:border-b-0 before:content-['']
              before:absolute before:w-0 before:h-0 before:border-l-[12px] before:border-l-transparent before:border-r-[12px] before:border-r-transparent before:border-b-[14px]
          before:left-1/2 before:-translate-x-1/2 before:top-[-13px] before:border-white">
                          {marketGroupItems.map((item, i) => (
                              <li key={item.marketGroup}
                                  className="relative h-[30px] w-full pl-[36px] flex items-center text-md text-[#495159] hover:bg-gray-100 cursor-pointer"
                                  onClick={() => handleSelectGroup(item.marketGroup)}>
                            <span>
                                {t(item.marketGroup)}
                            </span>
                              </li>
                          ))}
                      </ul>
                  </div>
              </>, document.body)}
      </div>
    )
};
